import { TWorksheetFieldSyncPayload } from 'types/worksheets/worksheetFields';
import { TWorksheetType } from 'types/worksheets/worksheets';

import { AllOrderFieldsResp } from '@api/responseModels/worksheet/allOrderFieldsResponse';
import { BlobResp } from '@api/responseModels/blob/blobResponse';
import { RequestResult } from '@api/responseModels/requestResult';
import { RequiredOrderFieldsResp } from '@api/responseModels/worksheet/requiredOrderFieldsResponse';
import { WorksheetFieldsResp } from '@api/responseModels/worksheet/worksheetFieldsResponse';
import { WorksheetResp } from '@api/responseModels/worksheet/worksheetsResponse';

import { CRMAPIBase } from '../crmApiBase';

class WorksheetGroup extends CRMAPIBase {
    async getWorksheets(
        params: { crm_id: number; page?: number; per_page?: number } & Record<string, unknown>
    ): Promise<RequestResult<WorksheetResp>> {
        const result = await this.get<WorksheetResp>(`worksheets`, params);
        return result;
    }

    /**
     * Состав полей одного наряд-листа CRM.
     * Контекст заказа не передаём: `WorksheetController::show` его не читает —
     * order-aware выборка живёт в `worksheets/get-all-fields`.
     */
    async getWorksheetFields(
        worksheetId: number,
        crm_id: number
    ): Promise<RequestResult<WorksheetFieldsResp>> {
        const result = await this.get<WorksheetFieldsResp>(`worksheets/${worksheetId}`, {
            crm_id,
        });
        return result;
    }

    async updateWorksheetFields(
        worksheetId: number,
        crm_id: number,
        order_fields: TWorksheetFieldSyncPayload[]
    ): Promise<RequestResult<WorksheetFieldsResp>> {
        const result = await this.post<WorksheetFieldsResp>(
            `worksheets/${worksheetId}/sync-fields`,
            {
                crm_id,
                order_fields,
            }
        );
        return result;
    }

    async getAllRequiredFields(crm_id): Promise<RequestResult<RequiredOrderFieldsResp>> {
        const result = await this.post<RequiredOrderFieldsResp>(
            `worksheets/get-all-required-fields`,
            {
                crm_id,
            }
        );
        return result;
    }

    async getAllFields(crm_id: number): Promise<RequestResult<AllOrderFieldsResp>> {
        const result = await this.get<AllOrderFieldsResp>(
            `worksheets/get-all-fields`,
            {
                crm_id,
            }
        );
        return result;
    }

    async getWorksheetPdf(params: {
        type: TWorksheetType;
        crm_id: number;
    }): Promise<RequestResult<BlobResp>> {
        let result = new RequestResult<BlobResp>();
        try {
            const response = await this._httpClient.request<BlobResp>({
                method: 'post',
                url: '/worksheets/generatePdf',
                data: params,
                responseType: 'blob',
            });
            result.statusCode = response.status;
            if (response.status >= 200 && response.status <= 300) {
                result.data = response.data;
            } else {
                result = CRMAPIBase.parseErrors<BlobResp>(response.status, response.data);
            }
        } catch (error) {
            result = CRMAPIBase.parseErrors<BlobResp>(error.response.status, error.response.data);
        }
        return result;
    }

    async createWorksheet(params: {
        crm_id: number;
        base_worksheet_id: number;
    }): Promise<RequestResult<WorksheetResp>> {
        const result = await this.post<WorksheetResp>(`worksheets`, params);
        return result;
    }

    async getAdminBaseWorksheetList(params: { page }): Promise<RequestResult<WorksheetResp>> {
        const result = await this.get<WorksheetResp>(`admin/base-worksheets`, params);
        return result; 
    }

    async getAdminBaseWorksheetPdf(params: {
        type: TWorksheetType;
    }): Promise<RequestResult<BlobResp>> {
        let result = new RequestResult<BlobResp>();
        try {
            const response = await this._httpClient.request<BlobResp>({
                method: 'post',
                url: 'admin/base-worksheets/generatePdf',
                data: params,
                responseType: 'blob',
            });
            result.statusCode = response.status;
            if (response.status >= 200 && response.status <= 300) {
                result.data = response.data;
            } else {
                result = CRMAPIBase.parseErrors<BlobResp>(response.status, response.data);
            }
        } catch (error) {
            result = CRMAPIBase.parseErrors<BlobResp>(error.response.status, error.response.data);
        }
        return result;
    }
}

export { WorksheetGroup };
